'use client'

import * as React from 'react'
import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import { cn } from '../../lib/cn'

function Popover ({ ...props }: PopoverPrimitive.Root.Props): JSX.Element {
  return <PopoverPrimitive.Root data-slot='popover' {...props} />
}

function PopoverTrigger ({ ...props }: PopoverPrimitive.Trigger.Props): JSX.Element {
  return <PopoverPrimitive.Trigger data-slot='popover-trigger' {...props} />
}

function PopoverContent ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'top',
  sideOffset = 16,
  container,
  arrow = false,
  zClass = 'z-1',
  ...props
}: PopoverPrimitive.Popup.Props &
Pick<
PopoverPrimitive.Positioner.Props,
'align' | 'alignOffset' | 'side' | 'sideOffset'
> & {
  container?: HTMLElement | ShadowRoot | null | React.RefObject<HTMLElement | ShadowRoot | null>
  arrow?: boolean
  zClass?: string
}): JSX.Element {
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className={`isolate ${zClass}`}
      >
        {arrow && (
          <PopoverPrimitive.Arrow
            className='group/arrow w-4 h-4 data-[side=bottom]:-top-4 data-[side=top]:-bottom-4 text-brand-primary-light'
          >
            <svg viewBox='0 0 16 16' className='group-data-[side=top]/arrow:hidden'>
              <path
                fill='white'
                stroke='currentColor'
                d='M 0 16 L 8 8 L 16 16'
              />
            </svg>
            <svg viewBox='0 0 16 16' className='group-data-[side=bottom]/arrow:hidden'>
              <path
                fill='white'
                stroke='currentColor'
                d='M 0 0 L 8 8 L 16 0'
              />
            </svg>
          </PopoverPrimitive.Arrow>
        )}
        <PopoverPrimitive.Popup
          data-slot='popover-content'
          className={cn(
            zClass, 'flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-md bg-popover p-4 text-sm text-popover-foreground shadow-md ring-1 ring-brand-primary-light outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

function PopoverHeader ({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  return (
    <div
      data-slot='popover-header'
      className={cn('flex flex-col gap-0.5 text-sm', className)}
      {...props}
    />
  )
}

function PopoverTitle ({ className, ...props }: PopoverPrimitive.Title.Props): JSX.Element {
  return (
    <PopoverPrimitive.Title
      data-slot='popover-title'
      className={cn('font-bold mb-3', className)}
      {...props}
    />
  )
}

function PopoverDescription ({
  className,
  ...props
}: PopoverPrimitive.Description.Props): JSX.Element {
  return (
    <PopoverPrimitive.Description
      data-slot='popover-description'
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger
}
